import { WorldMapMarkerDetailStyle } from '__generated__/globalTypes';
import cx from 'classnames';
import styles from 'components/map/WorldMapEdit.module.css';
import { getInitialViewState } from 'components/map/location/defaultMapLocation';
import { DirectionBuilder } from 'components/minute/builders/v2/DirectionBuilder';
import { LineBuilder } from 'components/minute/builders/v2/LineBuilder';
import { PolygonBuilder } from 'components/minute/builders/v2/PolygonBuilder';
import { WallBuilder } from 'components/minute/builders/v2/WallBuilder';
import { MouseDraggingSelection } from 'components/site/general/multiSelect/MouseDraggingSelection';
import { MultiMarkerSelection } from 'components/site/general/multiSelect/MultiMarkerSelection';
import { DisplaySelectedMarker } from 'components/site/general/singleSelect/DisplaySelectedMarker';
import { DetailModalBubble } from 'components/site/modals/DetailModalBubble';
import { ImageUploader } from 'components/site/process/ImageUploader';
import CSS from 'csstype';
import { CUSTOM_MARKER_BUILDERS } from 'customMarkers/customMarkerBuilders';
import { pinIconBuilder } from 'customMarkers/pinIconBuilder';
import { useFrameRateControl } from 'hooks/dev/useFrameRateControl';
import { useGlobalListeners } from 'hooks/listeners/useGlobalListeners';
import { useMarkerVisibility } from 'hooks/map/useMarkerVisibility';
import { usePreloadDefaultImages } from 'hooks/map/usePreloadDefaultImages';
import { useWorldControls } from 'hooks/map/useWorldControls';
import { useBackgroundImageMarker } from 'hooks/marker/useBackgroundImageMarker';
import { useWorldMarker } from 'hooks/marker/useWorldMarker';
import { useIsWide } from 'libs/utils/logic/ui/responsiveLogic';
import { GraphQLStylesMapToEnum } from 'libs/utils/mapping/worldGrahQLMaps';
import {
    mapIndoorStandForViewAndEdit,
    mapVariantIconForViewAndEdit
} from 'libs/utils/marker/mapIndoorStandForViewAndEdit';
import { mapPolygonForEdit, mapPolygonV2ForEdit } from 'libs/utils/marker/mapPolygonForEdit';
import { EWorldAction } from 'libs/utils/world/worldTypes';
import { isSpecialControlAction } from 'libs/utils/world/worldUtils';
import { MapMouseEvent } from 'mapbox-gl';
import { default as React, useContext, useEffect, useRef, useState } from 'react';
import { GiveMeMap, IWorldMarker } from 'react-give-me-map';
import { CtxEdit } from 'session/ctxEdit';
import { CtxGuideAdmin } from 'session/ctxGuideAdmin';
import { GLOBAL_MARKER_CONFIG } from 'static/config/globalMarkerConfig';
import styled from 'styled-components';

const MARKER_REDNER_OPTIMIZATION_PAGE_SIZE = 50;

export interface IProps {
    mapId: string;
    markers: IWorldMarker[];

    //Extra elements
    children?: React.ReactNode | React.ReactNode[];

    //React styling
    style?: CSS.Properties;
    className?: string;
}

export const WorldMapEdit = (props: IProps): JSX.Element => {
    const ctxEdit = useContext(CtxEdit);
    const wrapperMapRef = useRef<HTMLDivElement>();
    const controlsRef = useRef<HTMLDivElement[]>([]);
    // Due to this bug "style is not done loading"
    // https://github.com/visgl/react-map-gl/issues/2377
    const [canRender, setCanRender] = useState(false);

    const markerVisibility = useMarkerVisibility();
    const backgroundImageMarker = useBackgroundImageMarker();

    const isWide = useIsWide();

    const { onMapClick } = useWorldControls(controlsRef);
    const worldMarker = useWorldMarker();
    const singleSelectedMarker = worldMarker.getSingleMarkerIfOne();

    usePreloadDefaultImages(ctxEdit.mapRef);

    useEffect(() => {
        controlsRef.current = controlsRef.current.slice(0, 11);
    }, []);

    useGlobalListeners(wrapperMapRef, controlsRef);

    // Might be null!!, in standalone minute editor (out of Admin)!
    const ctxGuideAdmin = useContext(CtxGuideAdmin);
    const allStories = ctxGuideAdmin?.guide?.stories ?? [];

    // Uncomment this to see the FPS meter
    useFrameRateControl(ctxEdit.mapRef);

    const markers: IWorldMarker[] = ctxEdit.worldMarkers
        .map((e) => ({
            ...e,
            visible: markerVisibility.isMarkerVisibleInEdit(e),
            selectable: markerVisibility.isMarkerVisibleForSelectionInEdit(e)
        }))
        .map((m) => {
            if (m.elementType === 'indoor_stand') {
                return mapIndoorStandForViewAndEdit(m);
            }
            if (m.elementType === 'polygon') {
                return mapPolygonForEdit(m);
            }
            if (m.elementType === 'v2/polygon') {
                return mapPolygonV2ForEdit(m);
            }
            if (m.elementType === 'v2/variant_icon') {
                return mapVariantIconForViewAndEdit(m, allStories);
            }
            return m;
        });

    if (!ctxEdit.map || ctxEdit.map.worldMapId !== props.mapId) {
        // This is loading state
        return;
    }

    return (
        <>
            <div
                className={cx(styles.WorldMapEdit, props.className)}
                style={props.style}
                data-list-moments={'map'}
            >
                {props.children}

                <>
                    <div className={cx(styles.wrapperMapRef, 'givememap')} ref={wrapperMapRef}>
                        <GiveMeMap
                            mapRef={ctxEdit.mapRef}
                            isEditMode
                            isWide={isWide}
                            markerGlobalSettings={GLOBAL_MARKER_CONFIG[ctxEdit.map.worldMapId]}
                            map={{
                                initialViewState: getInitialViewState(ctxEdit.map, isWide),
                                light: {
                                    anchor: 'viewport',
                                    color: 'white',
                                    intensity: 0.2
                                },
                                bearing: ctxEdit.map.initial.rotation ?? 0,
                                // Pitch is only applied to View and not in Edit because it breaks
                                // some of the editing, especially images
                                pitch: 0,
                                mapboxAccessToken: process.env.REACT_APP_MAPBOX_TOKEN,
                                onClick: (e) => onMapClick(e as MapMouseEvent),
                                onLoad: () => {
                                    console.log('onLoad');
                                    setCanRender(true);
                                },
                                antialias: false,
                                mapStyle:
                                    GraphQLStylesMapToEnum[ctxEdit.map?.defaultStyles[0]] ??
                                    ctxEdit.map?.defaultStyles[0],
                                dragPan:
                                    !isSpecialControlAction(ctxEdit.worldAction) &&
                                    ctxEdit.worldAction !== EWorldAction.MULTISELECT,
                                scrollZoom: !isSpecialControlAction(ctxEdit.worldAction),
                                doubleClickZoom: !isSpecialControlAction(ctxEdit.worldAction)
                            }}
                            callbacks={{
                                onMarkersSelected: (ids) => {
                                    if (ids.length !== 1) {
                                        return;
                                    }

                                    const clickedReactMarker = ctxEdit.worldMarkers.find(
                                        (e) => e.elementType === 'react' && e.id === ids[0]
                                    );
                                    if (clickedReactMarker) {
                                        ctxEdit.setSelectedMarkers([ids[0]]);
                                    }

                                    const clickedVariantIconMarker = ctxEdit.worldMarkers.find(
                                        (e) =>
                                            e.elementType === 'v2/variant_icon' && e.id === ids[0]
                                    );

                                    if (clickedVariantIconMarker) {
                                        ctxEdit.setSelectedMarkers([ids[0]]);
                                    }
                                }
                            }}
                            markers={
                                canRender
                                    ? (ctxEdit.markerRenderPage
                                          ? markers
                                                .sort((e1, e2) => e1.order ?? 0 - e2.order ?? 0)
                                                .slice(
                                                    (ctxEdit.markerRenderPage - 1) *
                                                        MARKER_REDNER_OPTIMIZATION_PAGE_SIZE,
                                                    ctxEdit.markerRenderPage *
                                                        MARKER_REDNER_OPTIMIZATION_PAGE_SIZE
                                                )
                                          : markers
                                      )
                                          .concat(ctxEdit.markerCreation.marker)
                                          .concat(ctxEdit.showInitMapRectangle)
                                          .concat(
                                              backgroundImageMarker.getBackgroundMarker(
                                                  ctxEdit.map.defaultStyles,
                                                  ctxEdit.map.imageBackgroundUrl,
                                                  ctxEdit.map.imageBackgroundCoordinates
                                              )
                                          )
                                          .filter(Boolean)
                                    : []
                            }
                            selectedIds={[
                                ...ctxEdit.selectedMarkers,
                                ctxEdit.markerCreation?.marker?.id
                            ].filter(Boolean)}
                            customBuilders={{
                                reactMarkers: CUSTOM_MARKER_BUILDERS,
                                pinIcon: pinIconBuilder
                            }}
                            config={{
                                availableStyles: ctxEdit.map?.defaultStyles?.map(
                                    (e) => GraphQLStylesMapToEnum[e] ?? e
                                ),
                                geolocate: ctxEdit.map?.allowGeolocation,
                                groupMarkerProps: {
                                    textColor: ctxEdit.map.groupMarkerColor,
                                    backgroundColor: ctxEdit.map.groupMarkerBackground,
                                    clusterMaxZoom: ctxEdit.map.groupMarkerClusterMaxZoom,
                                    clusterRadius: ctxEdit.map.groupMarkerClusterRadius,
                                    autoHideIcons: ctxEdit.map.autoHideIcons
                                },
                                countriesFill:
                                    ctxEdit.map.fillCountryCodes?.length &&
                                    ctxEdit.map.fillCountryColor &&
                                    ctxEdit.map.fillCountryOpacity
                                        ? {
                                              countryCodes: ctxEdit.map.fillCountryCodes,
                                              opacity: ctxEdit.map.fillCountryOpacity,
                                              color: ctxEdit.map.fillCountryColor
                                          }
                                        : null
                            }}
                        >
                            {ctxEdit.mapRef?.current && (
                                <>
                                    {ctxEdit.worldAction === EWorldAction.MULTISELECT &&
                                        ctxEdit.mouseDraggingSelection?.left &&
                                        ctxEdit.mouseDraggingSelection?.width && (
                                            <MouseDraggingSelection />
                                        )}
                                </>
                            )}
                            {ctxEdit.map?.markerDetailStyle === WorldMapMarkerDetailStyle.BUBBLE &&
                                ctxEdit.worldMarkers && (
                                    <DetailModalBubble
                                        mapRef={ctxEdit.mapRef}
                                        allMarkers={ctxEdit.worldMarkers}
                                    />
                                )}
                        </GiveMeMap>
                    </div>

                    {ctxEdit.mapRef?.current && (
                        <>
                            {ctxEdit.worldAction === EWorldAction.LINE_V2 && <LineBuilder />}
                            {ctxEdit.worldAction === EWorldAction.DIRECTION && <DirectionBuilder />}
                            {ctxEdit.worldAction === EWorldAction.POLYGON_V2 && <PolygonBuilder />}
                            {ctxEdit.worldAction === EWorldAction.POLYGON_V2_WALL && (
                                <WallBuilder isLine={false} />
                            )}
                            {ctxEdit.worldAction === EWorldAction.LINE_V2_WALL && (
                                <WallBuilder isLine={true} />
                            )}
                        </>
                    )}

                    {singleSelectedMarker && ctxEdit.mapRef?.current && (
                        <>
                            <DisplaySelectedMarker
                                singleSelectedMarker={singleSelectedMarker}
                                controlsRef={controlsRef}
                            />
                        </>
                    )}

                    {ctxEdit.selectedMarkers?.length > 1 && (
                        <MultiMarkerSelection markerIds={ctxEdit.selectedMarkers} />
                    )}
                    <ImageUploader controlsRef={controlsRef} />
                    {ctxEdit.worldAction === EWorldAction.UPLOAD && (
                        <S_Upload
                            onClick={() => {
                                ctxEdit.setWorldAction(EWorldAction.SELECT);
                            }}
                        />
                    )}
                </>
            </div>
        </>
    );
};

const S_Upload = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
`;
